import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from 'services/apiClient';
import { decryptRestrictedMessagesInList } from 'utils/restrictedMessageCrypto';
import { normalizeConversationMessage } from './chatUtils';
import {
  buildMatchKey,
  findLocalSearchMatches,
  mergeSearchMatches,
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from './chatMessageSearchUtils';


export const useChatMessageSearch = ({
  api,
  contactId,
  messages,
  updateMessagesForContact,
  scrollToMessageRef,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [remotePage, setRemotePage] = useState(0);
  const [remoteHasMore, setRemoteHasMore] = useState(false);
  const [remoteTotal, setRemoteTotal] = useState(0);
  const [searchingRemote, setSearchingRemote] = useState(false);
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const debounceRef = useRef(null);
  const remoteMatchesRef = useRef([]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(String(query || '').trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const resetSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    setMatches([]);
    setCurrentIndex(-1);
    setRemotePage(0);
    setRemoteHasMore(false);
    setRemoteTotal(0);
    setSearchingRemote(false);
    setRemoteLoaded(false);
    remoteMatchesRef.current = [];
  }, []);

  const closeSearch = useCallback(() => {
    setOpen(false);
    resetSearch();
  }, [resetSearch]);

  useEffect(() => {
    resetSearch();
  }, [contactId, resetSearch]);

  const localMatches = useMemo(
    () => findLocalSearchMatches(messages, debouncedQuery),
    [messages, debouncedQuery]
  );

  useEffect(() => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      setMatches([]);
      setCurrentIndex(-1);
      setRemotePage(0);
      setRemoteHasMore(false);
      setRemoteTotal(0);
      setRemoteLoaded(false);
      remoteMatchesRef.current = [];
      return;
    }

    const merged = mergeSearchMatches(localMatches, remoteMatchesRef.current);
    setMatches(merged);
    setCurrentIndex(merged.length > 0 ? 0 : -1);
  }, [debouncedQuery, localMatches, remoteLoaded]);

  const fetchRemoteMatches = useCallback(
    async (page = 1) => {
      if (!contactId || debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
        return null;
      }

      setSearchingRemote(true);
      try {
        const response = await apiClient.get(`${api}message/${contactId}/search`, {
          params: {
            q: debouncedQuery,
            page,
            per_page: 10,
          },
        });

        const payload = response.data || {};
        const incoming = (payload.matches || []).map((match) => ({
          ...match,
          origin: 'remote',
        }));

        if (page === 1) {
          remoteMatchesRef.current = incoming;
        } else {
          remoteMatchesRef.current = mergeSearchMatches(
            remoteMatchesRef.current,
            incoming
          );
        }

        setRemotePage(payload.page || page);
        setRemoteHasMore(Boolean(payload.has_more));
        setRemoteTotal(Number(payload.total || 0));
        setRemoteLoaded(true);

        const merged = mergeSearchMatches(localMatches, remoteMatchesRef.current);
        setMatches(merged);
        setCurrentIndex((prev) => {
          if (merged.length === 0) {
            return -1;
          }
          if (prev < 0) {
            return 0;
          }
          return Math.min(prev, merged.length - 1);
        });

        return payload;
      } catch (error) {
        console.error('Conversation search failed:', error);
        return null;
      } finally {
        setSearchingRemote(false);
      }
    },
    [api, contactId, debouncedQuery, localMatches]
  );

  const ensureMessageLoaded = useCallback(
    async (match) => {
      if (!match?.id || !contactId || !updateMessagesForContact) {
        return true;
      }

      const exists = messages.some(
        (message) => message?.id === match.id && message?.msgType !== 'date'
      );
      if (exists) {
        return true;
      }

      setLoadingContext(true);
      try {
        const response = await apiClient.get(`${api}message/${contactId}/context`, {
          params: {
            message_id: match.id,
            source: match.source || 'report',
            per_page: 20,
          },
        });

        const contextMessages = await decryptRestrictedMessagesInList(
          (response.data?.msg || []).map((message) =>
            message?.msgType === 'date' ? message : normalizeConversationMessage(message)
          )
        );
        updateMessagesForContact(contactId, (prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNew = contextMessages.filter(
            (item) => item?.id && !existingIds.has(item.id)
          );
          if (uniqueNew.length === 0) {
            return prev;
          }
          return [...uniqueNew, ...prev];
        });

        return true;
      } catch (error) {
        console.error('Failed to load message context:', error);
        return false;
      } finally {
        setLoadingContext(false);
      }
    },
    [api, contactId, messages, updateMessagesForContact]
  );

  const scrollToCurrentMatch = useCallback(
    async (index, nextMatches = matches) => {
      const match = nextMatches[index];
      if (!match) {
        return;
      }

      await ensureMessageLoaded(match);

      window.setTimeout(() => {
        scrollToMessageRef?.current?.scrollToMessageId?.(match.id);
      }, 120);
    },
    [ensureMessageLoaded, matches, scrollToMessageRef]
  );

  const goToMatch = useCallback(
    async (index) => {
      if (index < 0 || index >= matches.length) {
        return;
      }
      setCurrentIndex(index);
      await scrollToCurrentMatch(index, matches);
    },
    [matches, scrollToCurrentMatch]
  );

  const searchFullConversation = useCallback(async () => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return;
    }
    await fetchRemoteMatches(1);
    const merged = mergeSearchMatches(localMatches, remoteMatchesRef.current);
    if (merged.length > 0) {
      setCurrentIndex(0);
      await scrollToCurrentMatch(0, merged);
    }
  }, [debouncedQuery, fetchRemoteMatches, localMatches, scrollToCurrentMatch]);

  const goToPreviousMatch = useCallback(async () => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return;
    }

    if (matches.length === 0) {
      if (!remoteLoaded) {
        await searchFullConversation();
      }
      return;
    }

    if (currentIndex > 0) {
      await goToMatch(currentIndex - 1);
      return;
    }

    if (!remoteLoaded) {
      await searchFullConversation();
    }
  }, [
    currentIndex,
    debouncedQuery,
    goToMatch,
    matches.length,
    remoteLoaded,
    searchFullConversation,
  ]);

  const goToNextMatch = useCallback(async () => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return;
    }

    if (matches.length === 0) {
      if (!remoteLoaded) {
        await searchFullConversation();
      }
      return;
    }

    if (currentIndex < matches.length - 1) {
      await goToMatch(currentIndex + 1);
      return;
    }

    if (remoteHasMore) {
      const payload = await fetchRemoteMatches(remotePage + 1);
      const merged = mergeSearchMatches(localMatches, remoteMatchesRef.current);
      if (payload && merged.length > matches.length) {
        await goToMatch(currentIndex + 1);
      }
    }
  }, [
    currentIndex,
    debouncedQuery,
    fetchRemoteMatches,
    goToMatch,
    localMatches,
    matches.length,
    remoteHasMore,
    remoteLoaded,
    remotePage,
    searchFullConversation,
  ]);

  useEffect(() => {
    if (!open || debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH || matches.length === 0) {
      return;
    }
    scrollToCurrentMatch(0, matches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, open]);

  const statusText = useMemo(() => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return `Type at least ${MIN_SEARCH_QUERY_LENGTH} characters`;
    }

    if (matches.length > 0 && currentIndex >= 0) {
      const displayTotal = remoteLoaded && remoteTotal > 0
        ? Math.max(remoteTotal, matches.length)
        : matches.length;
      return `${currentIndex + 1} of ${displayTotal}`;
    }

    if (searchingRemote || loadingContext) {
      return 'Searching…';
    }

    if (remoteLoaded) {
      return 'No matches';
    }

    return 'Not found';
  }, [
    currentIndex,
    debouncedQuery,
    loadingContext,
    matches.length,
    remoteLoaded,
    remoteTotal,
    searchingRemote,
  ]);

  const canGoPrevious = useMemo(() => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return false;
    }
    if (searchingRemote || loadingContext) {
      return false;
    }
    if (remoteLoaded && remoteTotal === 0 && matches.length === 0) {
      return false;
    }
    if (matches.length > 0 && currentIndex > 0) {
      return true;
    }
    if (matches.length === 0 && !remoteLoaded) {
      return true;
    }
    if (currentIndex === 0 && !remoteLoaded && matches.length > 0) {
      return true;
    }
    return false;
  }, [
    currentIndex,
    debouncedQuery,
    loadingContext,
    matches.length,
    remoteLoaded,
    remoteTotal,
    searchingRemote,
  ]);

  const canGoNext = useMemo(() => {
    if (debouncedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      return false;
    }
    if (searchingRemote || loadingContext) {
      return false;
    }
    if (remoteLoaded && remoteTotal === 0 && matches.length === 0) {
      return false;
    }
    if (matches.length > 0 && currentIndex < matches.length - 1) {
      return true;
    }
    if (remoteHasMore) {
      return true;
    }
    if (matches.length === 0 && !remoteLoaded) {
      return true;
    }
    return false;
  }, [
    currentIndex,
    debouncedQuery,
    loadingContext,
    matches.length,
    remoteHasMore,
    remoteLoaded,
    remoteTotal,
    searchingRemote,
  ]);

  const activeMatch = currentIndex >= 0 ? matches[currentIndex] : null;

  return {
    open,
    setOpen,
    query,
    setQuery,
    debouncedQuery,
    matches,
    currentIndex,
    activeMatch,
    activeMatchKey: activeMatch ? buildMatchKey(activeMatch) : null,
    statusText,
    searchingRemote,
    loadingContext,
    remoteLoaded,
    remoteHasMore,
    showSearchFull:
      debouncedQuery.length >= MIN_SEARCH_QUERY_LENGTH
      && matches.length === 0
      && !remoteLoaded
      && !searchingRemote,
    canGoPrevious,
    canGoNext,
    closeSearch,
    resetSearch,
    goToPreviousMatch,
    goToNextMatch,
    searchFullConversation,
  };
};
